import { observer } from 'mobx-react';
import { useStore } from 'app/context';
import { useMemo } from 'react';
import colors from 'assets/scss/colors.module.scss';
import { TimeSeriesChart, ITimeSeriesChartConfig } from 'app/components/common/charts/TimeSeriesChart';

interface IAmisNowHistoryChart {
	agtId?: number;
	agtIds?: number[];
}

interface AmisNowChartData {
	bm: string;
	bmRaw: number;
	aufgabenGesamt: number;
	aufgabenAdz: number;
	adzAufgabenquote: number;
}

export const AmisNowHistoryChart = observer((props: IAmisNowHistoryChart) => {
	const { agtAmisNowUiStore } = useStore();
	const { agtId, agtIds } = props;

	// Build chart configuration
	const chartConfig: ITimeSeriesChartConfig<AmisNowChartData> = useMemo(
		() => ({
			title: 'AMIS.NOW Aufgaben Entwicklung (letzte 12 Monate)',
			tag: 'AMIS.NOW',
			tagColor: 'turquoise',
			height: 400,

			xAxisField: 'bm',
			xAxisRotation: 45,

			// Define Y-axes
			yAxes: [
				{
					name: 'Anzahl Aufgaben',
					position: 'left',
				},
				{
					name: 'AdZ Aufgabenquote (%)',
					position: 'right',
					max: 100,
					formatter: (v) => `${v}%`,
					valueType: 'percentage',
					splitLine: false,
				},
			],

			// Define derived fields (calculated at render time)
			derivedFields: [
				{
					fieldName: 'adzAufgabenquotePercent',
					calculate: (d) => d.adzAufgabenquote * 100,
				},
				{
					fieldName: 'sonstigeAufgaben',
					calculate: (d) => d.aufgabenGesamt - d.aufgabenAdz,
				},
			],

			// Define series
			series: [
				{
					name: 'Aufgaben AdZ',
					dataField: 'aufgabenAdz',
					yAxisIndex: 0,
					chartType: 'bar',
					color: colors.turquoise,
					stack: 'tasks',
					barMaxWidth: 40,
				},
				{
					name: 'Sonstige Aufgaben',
					dataField: 'sonstigeAufgaben',
					yAxisIndex: 0,
					chartType: 'bar',
					color: colors.greyLight,
					stack: 'tasks',
					barMaxWidth: 40,
				},
				{
					name: 'AdZ Aufgabenquote (%)',
					dataField: 'adzAufgabenquotePercent',
					yAxisIndex: 1,
					chartType: 'line',
					smooth: true,
					color: colors.red,
					symbolSize: 8,
					lineStyle: { width: 2 },
				},
			],

			// Data loader function
			dataLoader: async (ids) => {
				if (Array.isArray(ids)) {
					return agtAmisNowUiStore.loadAmisNowHistoryLast12MonthsAggregated(ids);
				} else {
					return agtAmisNowUiStore.loadAmisNowHistoryLast12Months(ids);
				}
			},
		}),
		[agtAmisNowUiStore],
	);

	return <TimeSeriesChart config={chartConfig} agtId={agtId} agtIds={agtIds} />;
});
